import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TrainFrontIcon } from 'lucide-react';
const CREMORNE_CENTER: [number, number] = [-37.828, 144.993];
interface MapMarker {
  position: [number, number];
  label: string;
  type: 'train' | 'tram' | 'amenity';
}
const markers: MapMarker[] = [
// Train stations
{
  position: [-37.824, 144.9893],
  label: 'Richmond Station',
  type: 'train'
}, {
  position: [-37.8268, 144.9993],
  label: 'East Richmond Station',
  type: 'train'
}, {
  position: [-37.8282, 145.0073],
  label: 'Burnley Station',
  type: 'train'
},
// Tram routes
{
  position: [-37.8225, 144.992],
  label: 'Swan St / Church St Tram',
  type: 'tram'
}, {
  position: [-37.8225, 144.998],
  label: 'Swan St / Cremorne St Tram',
  type: 'tram'
}, {
  position: [-37.831, 144.987],
  label: 'Church St Tram (South)',
  type: 'tram'
}, {
  position: [-37.8225, 145.004],
  label: 'Swan St / Balmain St Tram',
  type: 'tram'
},
// Key amenity
{
  position: [-37.8295, 144.9925],
  label: 'CityLink Access',
  type: 'amenity'
}, {
  position: [-37.826, 144.985],
  label: 'Church St Precinct',
  type: 'amenity'
}];
function createMarkerIcon(type: MapMarker['type']): L.DivIcon {
  const colors = {
    train: '#C9A84C',
    tram: '#C9A84C',
    amenity: '#888888'
  };
  const sizes = {
    train: 12,
    tram: 8,
    amenity: 8
  };
  const color = colors[type];
  const size = sizes[type];
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      border: 2px solid ${color};
      border-radius: 50%;
      box-shadow: 0 0 8px ${color}40;
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
export function MapSection() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const map = L.map(mapRef.current, {
      center: CREMORNE_CENTER,
      zoom: 15,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false
    });
    // Dark tile layer to match brand
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19
    }).addTo(map);
    // Zoom control on right
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    // Add markers
    markers.forEach(m => {
      const marker = L.marker(m.position, {
        icon: createMarkerIcon(m.type)
      }).addTo(map);
      const typeLabel = m.type === 'train' ? '🚆' : m.type === 'tram' ? '🚊' : '📍';
      marker.bindPopup(`<div style="
          font-family: 'DM Mono', monospace;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #0C0C0C;
          padding: 2px 0;
        ">
          <span style="margin-right: 4px;">${typeLabel}</span>${m.label}
        </div>`, {
        className: 'custom-popup',
        closeButton: false
      });
    });
    // Subject property marker (larger, distinct)
    const propertyIcon = L.divIcon({
      className: 'property-marker',
      html: `<div style="
        width: 18px;
        height: 18px;
        background: #C9A84C;
        border: 3px solid #FFFFFF;
        border-radius: 50%;
        box-shadow: 0 0 16px #C9A84C80, 0 0 32px #C9A84C40;
      "></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    });
    L.marker(CREMORNE_CENTER, {
      icon: propertyIcon
    }).addTo(map).bindPopup(`<div style="
          font-family: 'DM Mono', monospace;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #0C0C0C;
          font-weight: bold;
          padding: 2px 0;
        ">
          Subject Property
        </div>`, {
      className: 'custom-popup',
      closeButton: false
    });
    mapInstance.current = map;
    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, []);
  return <section className="bg-brand-black py-16 md:py-32 px-5 md:px-12 lg:px-24" data-id="element-60">
      <div className="max-w-7xl mx-auto" data-id="element-61">
        {/* Header */}
        <div className="mb-10 md:mb-16" data-id="element-62">
          <p className="font-mono uppercase tracking-label text-brand-grey text-xs mb-5 md:mb-6" data-id="element-63">
            CONNECTIVITY.
          </p>
          <h2 className="font-display uppercase text-brand-white text-3xl md:text-6xl leading-[0.9]" data-id="element-64">
            <span className="block" data-id="element-65">PUBLIC TRANSPORT</span>
            <span className="block" data-id="element-66">& AMENITY.</span>
          </h2>
        </div>

        {/* Map */}
        <div className="relative border border-brand-grey/20 overflow-hidden" data-id="element-67">
          <div ref={mapRef} className="w-full h-[400px] md:h-[500px]" data-id="element-68" />
        </div>

        {/* Legend */}
        <div className="mt-6 md:mt-8 flex flex-wrap gap-6 md:gap-10" data-id="element-69">
          <div className="flex items-center gap-2.5" data-id="element-70">
            <div className="w-3 h-3 rounded-full bg-brand-gold border-2 border-brand-white shadow-[0_0_8px_rgba(201,168,76,0.5)]" data-id="element-71" />
            <span className="font-mono text-brand-grey text-[10px] md:text-xs uppercase tracking-wider" data-id="element-72">
              Subject Property
            </span>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-73">
            <div className="w-3 h-3 rounded-full bg-brand-gold" data-id="element-74" />
            <span className="font-mono text-brand-grey text-[10px] md:text-xs uppercase tracking-wider" data-id="element-75">
              Train Station
            </span>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-76">
            <div className="w-2 h-2 rounded-full bg-brand-gold" data-id="element-77" />
            <span className="font-mono text-brand-grey text-[10px] md:text-xs uppercase tracking-wider" data-id="element-78">
              Tram Stop
            </span>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-79">
            <div className="w-2 h-2 rounded-full bg-brand-grey" data-id="element-80" />
            <span className="font-mono text-brand-grey text-[10px] md:text-xs uppercase tracking-wider" data-id="element-81">
              Key Amenity
            </span>
          </div>
        </div>
      </div>
    </section>;
}